'use client';

import { useContext } from 'react';
import { MultistepFormContext } from '../context/MultistepFormContext';

/**
 * Hook to access multistep form navigation methods from within a step component.
 * Use this hook to connect individual step forms to the multistep navigation.
 *
 * @returns Navigation methods and state for the multistep form
 *
 * @example
 * ```tsx
 * // Step component with its own form element
 * function MyStep({ onSubmit }: { onSubmit: (data: FormData) => void }) {
 *   const { goToNextStep, goToPreviousStep, isFirstStep, isLastStep } = useMultistepFormNavigation();
 *
 *   const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
 *     event.preventDefault();
 *     const formData = new FormData(event.currentTarget);
 *
 *     // Do your validation/processing
 *     onSubmit(formData);
 *
 *     // Navigate to next step
 *     if (!isLastStep) {
 *       goToNextStep();
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <input name="email" type="email" required />
 *       <button type="submit">
 *         {isLastStep ? 'Complete' : 'Continue'}
 *       </button>
 *       {!isFirstStep && (
 *         <button type="button" onClick={goToPreviousStep}>
 *           Back
 *         </button>
 *       )}
 *     </form>
 *   );
 * }
 * ```
 */
function useMultistepFormNavigation() {
  const context = useContext(MultistepFormContext);

  if (!context) {
    throw new Error('useMultistepFormNavigation must be used within a MultistepFormProvider');
  }

  return {
    currentStepIndex: context.currentStepIndex,
    steps: context.steps,
    isFirstStep: context.isFirstStep,
    isLastStep: context.isLastStep,
    goToNextStep: context.goToNextStep,
    goToPreviousStep: context.goToPreviousStep,
    goToStep: context.goToStep,
    skipToStep: context.skipToStep,
    isLoading: context.isLoading,
    setIsLoading: context.setIsLoading,
    formData: context.formData,
    updateFormData: context.updateFormData,
    formErrors: context.formErrors,
    status: context.status,
    setStatus: context.setStatus,
  };
}

export { useMultistepFormNavigation };
